import {useAutoAnimate} from '@formkit/auto-animate/react';
import {type ChangeEventHandler, type FormEventHandler, memo, useCallback, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {parseUSPhoneNumber, valid_email as validEmail} from '~/common/utils';
import {getPydanticError} from '~/common/utils/errors';
import type {GuestlistRouteParams} from '~/ViewInvitation/components/Guestlist/GuestlistOverlay';
import {GuestlistOverlayHeader} from '~/ViewInvitation/components/Guestlist/GuestlistOverlayHeader';
import {useGuestApi} from '~/ViewInvitation/components/Guestlist/hooks/useGuestApi';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {
  DeliveryStatus,
  type Guest,
  type InviteMethod,
  isHostGuest,
} from '~/ViewInvitation/types/guests';
import {Button} from '~library/atoms/Button';
import {GenericSpinner} from '~library/atoms/GenericSpinner';
import {Input} from '~library/atoms/Input';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {CheckmarkIcon} from '~library/svg/v2/CheckmarkIcon';
import {ErrorCircleIcon} from '~library/svg/v2/ErrorCircleIcon';
import {TrashIcon} from '~library/svg/v2/TrashIcon';

import * as styles from './UpdateGuest.module.scss';

const isEmail = (email: string) => {
  return validEmail(email).is_valid;
};

interface UpdateGuestProps {
  onClose: () => void;
}

export const UpdateGuest = memo(({onClose}: UpdateGuestProps) => {
  const [animate] = useAutoAnimate();
  const history = useHistory();
  const {userId} = useParams<GuestlistRouteParams>();
  const {
    state: {guestlist},
  } = useGuestlistDataContext();
  const guest = guestlist?.find((g: Guest) => g.userId === userId);
  const [guestName, setGuestName] = useState<string>(guest?.name || '');
  const [emailOrPhone, setEmailOrPhone] = useState<string>(
    isHostGuest(guest) ? guest?.phone || guest?.email || '' : ''
  );
  const [nameError, setNameError] = useState('');
  const [emailOrPhoneError, setEmailOrPhoneError] = useState('');
  const [deliveryStatus] = useState<DeliveryStatus | null>(
    isHostGuest(guest) ? guest?.deliveryStatus : null
  );

  // Data
  const {deleteGuest, updateGuest, isDeleting, isUpdating} = useGuestApi(userId);

  // Actions
  const goBack = useCallback(() => {
    if (
      deliveryStatus === DeliveryStatus.FAILED ||
      (isHostGuest(guest) && guest?.deliveryStatus === DeliveryStatus.FAILED)
    ) {
      history.replace({
        pathname: `/guests`,
        search: history.location.search,
      });
    } else {
      history.replace({
        pathname: `/guests/${userId}`,
        search: history.location.search,
      });
    }
  }, [history, userId, deliveryStatus, guest]);

  const onGuestNameChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setGuestName(e.target.value);
  }, []);

  const onGuestEmailPhoneChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setEmailOrPhone(e.target.value);
  }, []);

  const validateName = useCallback(() => {
    if (!guestName || guestName.length === 0) {
      setNameError('Please enter a name');
      return false;
    }
    return true;
  }, [guestName]);

  const validateEmailPhone = useCallback(() => {
    const strippedPhoneNumber = parseUSPhoneNumber(emailOrPhone).digits; // Convert formatted phone back to guest model's format

    // If no emailPhone field exists or email/phone didn't change, no need to validate these fields
    const guestEmail = isHostGuest(guest) ? guest?.email : null;
    const guestPhone = isHostGuest(guest) ? guest?.phone : null;
    if (!emailOrPhone || guestEmail === emailOrPhone || guestPhone === strippedPhoneNumber) {
      return true;
    }

    const isValidPhone = parseUSPhoneNumber(emailOrPhone).is_valid;
    const isValidEmail = isEmail(emailOrPhone);

    if (isValidPhone) {
      setEmailOrPhoneError('');
      return {
        inviteMethod: 'sms' as InviteMethod,
        phone: emailOrPhone,
      };
    }
    if (isValidEmail) {
      setEmailOrPhoneError('');
      return {
        inviteMethod: 'email' as InviteMethod,
        email: emailOrPhone,
      };
    }
    setEmailOrPhoneError('Please provide a valid email address or phone number');
    return false;
  }, [guest, emailOrPhone]);

  const doResend: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault();
      const validName = validateName();
      const emailOrPhoneData: Partial<Guest> | boolean = validateEmailPhone();
      if (!validName || !emailOrPhoneData) return;

      const guest = {
        ...(emailOrPhoneData as Partial<Guest>),
        name: guestName,
      };
      try {
        await updateGuest(guest);
        toastAlert('Guest successfully updated', {
          icon: <CheckmarkIcon />,
          type: 'success',
        });
        goBack();
      } catch (e: unknown) {
        evite.error('Failed to update guest', e);
        const message = getPydanticError(e, '');
        toastAlert(`Unable to resend guest. Please try again.\n${message}`, {
          icon: <AlertLineIcon />,
          type: 'error',
        });
      }
    },
    [validateEmailPhone, updateGuest, validateName, goBack, guestName]
  );

  const doRemoveGuest = useCallback(async () => {
    try {
      await deleteGuest();
      toastAlert('Guest successfully deleted', {
        icon: <CheckmarkIcon />,
        type: 'success',
      });
    } catch (e: unknown) {
      evite.error('Failed to delete guest', e);
      const message = getPydanticError(e, '');
      toastAlert(`Unable to delete guest. Please try again.\n${message}`, {
        icon: <AlertLineIcon />,
        type: 'error',
      });
    }
  }, [deleteGuest]);

  if (!guest) return null;

  return (
    <form className={styles.container} onSubmit={doResend}>
      <GuestlistOverlayHeader onClose={onClose} goBack={goBack} />
      <div className={styles.wrapper} ref={animate}>
        {isHostGuest(guest) && guest.deliveryStatus === DeliveryStatus.FAILED && (
          <div className={styles.failedNotice}>
            <ErrorCircleIcon color="feedbackError" ratio={0.75} />
            <Typography
              variant="paragraph2"
              color="feedbackError"
              className={styles.failedNoticeText}
            >
              <strong>Your Invitation couldn’t be delivered</strong>
              <br />
              Hmm… the email or phone number you entered could not be reached.
            </Typography>
          </div>
        )}
        <div className={styles.input}>
          <Input
            data-qa-id="guest-name"
            label="Guest Name"
            value={guestName}
            onChange={onGuestNameChange}
            min={1}
            error={nameError}
          />
          <Input
            data-qa-id="guest-contact"
            label="Email / Phone Number"
            value={emailOrPhone}
            onChange={onGuestEmailPhoneChange}
            min={3}
            error={emailOrPhoneError}
          />
        </div>
        <div className={styles.removeButton}>
          <Button
            data-qa-id="remove-guest-button"
            variant="link"
            className="remove-button"
            onClick={doRemoveGuest}
            disabled={isDeleting || isUpdating}
          >
            {isDeleting ? <GenericSpinner size={16} /> : <TrashIcon ratio={0.75} />} Remove guest
          </Button>
        </div>
        <div className={styles.actionRow}>
          <Button
            data-qa-id="cancel-button"
            variant="secondary"
            size="large"
            onClick={goBack}
            disabled={isDeleting || isUpdating}
          >
            Cancel
          </Button>
          <Button
            data-qa-id="resend-button"
            variant="primary"
            size="large"
            disabled={isDeleting || isUpdating}
            type="submit"
            disableProcessing
            formSubmit
          >
            {isUpdating && <GenericSpinner size={16} />} Resend
          </Button>
        </div>
      </div>
    </form>
  );
});

UpdateGuest.displayName = 'UpdateGuest';
